import axios from "axios";
import { OriginFormValues } from "./OriginForm";

interface Origin {
    origin_id?: number;
    origin_type_id: string | number;
    is_enabled: boolean;
}

interface OriginType {
    origin_type_id: number;
    analytics_type: number;
}

interface PcntOrder {
    analytics_type: number;
    order_count: number;
}

interface Result {
    valid: boolean;
    errorMessage: string;
}

export async function checkOriginConfigurationLimit2(
    oldOrigin: OriginFormValues | undefined, 
    newOrigin: Origin, 
    rawOriginTypes: OriginType[], 
    allOrigins: Origin[], 
    orderOrigins: PcntOrder[],
    ): Promise<Result> {
    
    // Skip check if origin is disabled
    if (!newOrigin.is_enabled) {
        return { valid: true, errorMessage: '' };
    }
    // Skip check if is_enabled hasn't changed (UPDATE operation)
    if (oldOrigin && oldOrigin.is_enabled === newOrigin.is_enabled) {
        return { valid: true, errorMessage: '' };
    }
    
    // Get analytics type for the origin type
    const newOrigin_OriginType = rawOriginTypes.find(
        (ot) => ot.origin_type_id === Number(newOrigin.origin_type_id)
    );
    
    if (!newOrigin_OriginType) {
        return { valid: false, errorMessage: "Origin type not found" };
    }
    
    // Count total enabled origins of the same analytics type
    let totalEnabled = allOrigins.filter((o) => {
        const ot = rawOriginTypes.find(
            (ot) => ot.origin_type_id === Number(o.origin_type_id)
        );
        return ot?.analytics_type === newOrigin_OriginType.analytics_type && o.is_enabled;
    }).length;

    if (oldOrigin && !oldOrigin.is_enabled) {
        totalEnabled += 1; // If we are enabling an origin, we should count it in totalEnabled
    }
    
    // Get order count for the analytics type
    const order = orderOrigins.find(
        (o) => o.analytics_type === newOrigin_OriginType.analytics_type
    );
    const orderCount = order?.order_count ?? 0;
    
    if (totalEnabled > orderCount) {
        return { valid: false, errorMessage: "Too many origins enabled" };
    }

    return { valid: true, errorMessage: '' };
}

export async function checkOriginConfigurationLimit(newOrigin: Origin): Promise<Result> {

    // Skip check if origin is disabled
    if (!newOrigin.is_enabled) {
        return { valid: true, errorMessage: '' };
    }
    
    const oldOrigin = newOrigin?.origin_id ? await getOrigin(newOrigin?.origin_id) : null;
    const originTypes = await getOriginTypes();
    const origins = await getOrigins();
    const pcntOrders = await getPcntOrders();
    // const [oldOrigin, originTypes, origins, pcntOrders] = await Promise.all([
    //     getOrigin(newOrigin?.origin_id),
    //     getOriginTypes(),
    //     getOrigins(),
    //     getPcntOrders(),
    // ]);

    // Skip check if is_enabled hasn't changed (UPDATE operation)
    if (oldOrigin && oldOrigin.is_enabled === newOrigin.is_enabled) {
        return { valid: true, errorMessage: '' };
    }

    // Get analytics type for the origin type
    const newOrigin_OriginType = originTypes.find(
        (ot) => ot.origin_type_id === newOrigin.origin_type_id
    );
    if (!newOrigin_OriginType) {
        return { valid: false, errorMessage: "Origin type not found" };
    }

    // Count total enabled origins of the same analytics type
    const totalEnabled = origins.filter((o) => {
        const ot = originTypes.find(
            (ot) => ot.origin_type_id === Number(o.origin_type_id)
        );
        return ot?.analytics_type === newOrigin_OriginType.analytics_type && o.is_enabled;
    }).length;

    // Get order count for the analytics type
    const order = pcntOrders.find(
        (o) => o.analytics_type === newOrigin_OriginType.analytics_type
    );
    const orderCount = order?.order_count ?? 0;

    // Check limit
    if (totalEnabled > orderCount) {
        return { valid: false, errorMessage: "Too much enabled origins" };
    }
    return { valid: true, errorMessage: '' };
}

const getOrigin = async (origin_id: number): Promise<Origin | null> => {
    console.log(`[originApi] getOrigin called for origin_id: ${origin_id}`);
    const res = await axios.get<Origin[]>(`/origin/?origin_id=${origin_id}`);
    return res.data.length > 0 ? res.data[0] : null;
};

const getOriginTypes = async (): Promise<OriginType[]> => {
    console.log(`[originApi] getOriginTypes called`);
    const res = await axios.get<OriginType[]>(`/origin_type/`);
    return res.data;
}

const getOrigins = async (): Promise<Origin[]> => {
    console.log(`[originApi] getOrigins called`);
    const res = await axios.get<Origin[]>(`/origin/`);
    return res.data;
}

const getPcntOrders = async (): Promise<PcntOrder[]> => {
    console.log(`[originApi] getPcntOrders called`);
    const res = await axios.get<PcntOrder[]>(`/order/count_by_analytics_type/`);
    return res.data;
}